import React, { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "react-toastify";
import {
  cancelSaleQrPayment,
  getSaleQrPaymentStatus,
} from "../../services/sale.service";

const POLL_MS = 3000;

const toImageSrc = (img) => {
  if (!img) return null;
  return /^(data:|https?:)/.test(img) ? img : `data:image/png;base64,${img}`;
};

const SaleQrPaymentModal = ({ businessUuid, session, onPaid, onClose }) => {
  const [status, setStatus] = useState("PENDING");
  const [secondsLeft, setSecondsLeft] = useState(0);
  const doneRef = useRef(false);

  useEffect(() => {
    if (!session) return undefined;
    doneRef.current = false;
    setStatus("PENDING");

    const poll = async () => {
      if (doneRef.current) return;
      try {
        const data = await getSaleQrPaymentStatus(businessUuid, session.sessionId);
        if (doneRef.current) return;
        if (data.status === "COMPLETED") {
          doneRef.current = true;
          setStatus("COMPLETED");
          onPaid(data.sale);
        } else if (["FAILED", "EXPIRED"].includes(data.status)) {
          doneRef.current = true;
          setStatus(data.status);
        }
      } catch (e) {
        console.error("QR status poll failed", e);
      }
    };

    const pollTimer = setInterval(poll, POLL_MS);
    const tickTimer = setInterval(() => {
      setSecondsLeft(
        Math.max(0, Math.floor((new Date(session.expiresAt) - Date.now()) / 1000)),
      );
    }, 1000);
    return () => {
      doneRef.current = true;
      clearInterval(pollTimer);
      clearInterval(tickTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.sessionId]);

  if (!session) return null;

  const handleCancel = async () => {
    if (status === "PENDING") {
      doneRef.current = true;
      try {
        const data = await cancelSaleQrPayment(businessUuid, session.sessionId);
        if (data.status === "COMPLETED") {
          onPaid(data.sale);
          return;
        }
      } catch (e) {
        console.error("Cancel QR failed", e);
      }
    }
    onClose();
  };

  const imgSrc = toImageSrc(session.qrImage);
  const checkoutUrl = session.checkoutUrl;
  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
  const ss = String(secondsLeft % 60).padStart(2, "0");

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
      <div className="w-[90%] max-w-sm rounded-lg bg-white p-6 text-center shadow-xl dark:bg-gray-800 dark:text-white">
        <h3 className="mb-1 text-lg font-bold">Scan &amp; Pay</h3>
        <p className="mb-4 text-2xl font-bold">₹{session.amount}</p>

        {status === "PENDING" && (
          <>
            <div className="mx-auto flex h-56 w-56 items-center justify-center bg-white p-2">
              {imgSrc ? (
                <img src={imgSrc} alt="UPI QR" className="h-full w-full object-contain" />
              ) : (
                <QRCodeSVG value={session.upiLink || ""} size={208} />
              )}
            </div>
            <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
              Waiting for payment... expires in {mm}:{ss}
            </p>
            <p className="text-xs text-gray-500">
              The sale is created automatically once the payment is received.
            </p>
            {checkoutUrl && (
              <div className="mt-3 flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => window.open(checkoutUrl, "_blank", "noopener,noreferrer")}
                  className="rounded bg-blue-600 px-4 py-2 font-semibold text-white"
                >
                  Open payment page (UPI, Card, Netbanking)
                </button>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard?.writeText(checkoutUrl);
                    toast.success("Payment link copied");
                  }}
                  className="rounded border border-blue-600 px-4 py-2 font-semibold text-blue-600"
                >
                  Copy payment link
                </button>
              </div>
            )}
          </>
        )}
        {status === "COMPLETED" && (
          <p className="py-8 font-semibold text-green-600">Payment received. Creating sale...</p>
        )}
        {status === "EXPIRED" && (
          <p className="py-8 font-semibold text-red-600">QR expired. No payment received.</p>
        )}
        {status === "FAILED" && (
          <p className="py-8 font-semibold text-red-600">
            Payment received but the sale could not be created. Please contact support
            with reference {session.sessionId}.
          </p>
        )}

        <button
          type="button"
          onClick={handleCancel}
          className="mt-4 rounded border border-gray-400 px-4 py-2 font-semibold"
        >
          {status === "PENDING" ? "Cancel" : "Close"}
        </button>
      </div>
    </div>
  );
};

export default SaleQrPaymentModal;
